import React, { useState } from 'react';

// Formspree endpoint. Form set to deliver to info@ononegroup.com.
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mpqgwaqq';

// Services offered (matches the site navigation). Used for the Subject dropdown.
const SERVICE_OPTIONS = [
    'Web Development',
    'IT Consulting',
    'Software Solutions',
    'App Development',
    'AI & Automation',
    'Real Estate',
    'Investing',
    'Other'
];

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [status, setStatus] = useState({ state: 'idle', message: '' });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate the visitor entered a real email before sending.
        if (!isValidEmail(formData.email)) {
            setStatus({ state: 'error', message: 'Please enter a valid email address so we can reply.' });
            return;
        }

        setStatus({ state: 'sending', message: '' });

        try {
            const res = await fetch(FORMSPREE_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    subject: formData.subject,
                    message: formData.message,
                    _replyto: formData.email,
                    _subject: `New inquiry (${formData.subject}) from ${formData.name}`
                })
            });

            if (res.ok) {
                setStatus({ state: 'success', message: "Thanks! Your message has been sent. We'll get back to you soon." });
                setFormData({ name: '', email: '', subject: '', message: '' });
            } else {
                const data = await res.json().catch(() => ({}));
                const msg = (data.errors && data.errors.map((er) => er.message).join(', ')) ||
                    'Something went wrong. Please try again or email us directly at info@ononegroup.com.';
                setStatus({ state: 'error', message: msg });
            }
        } catch (err) {
            setStatus({ state: 'error', message: 'Network error. Please try again or email us directly at info@ononegroup.com.' });
        }
    };

    const contactInfo = [
        {
            icon: '📧',
            title: 'Email',
            value: 'info@ononegroup.com',
            link: 'mailto:info@ononegroup.com'
        }
    ];

    return (
        <div className="page">
            {/* Hero Section */}
            <section className="page-hero">
                <div className="page-hero-content container">
                    <h1>Get In <span className="text-gradient">Touch</span></h1>
                    <p>
                        Have a project in mind or questions about our services?
                        We'd love to hear from you.
                    </p>
                </div>
            </section>

            {/* Contact Section */}
            <section className="section">
                <div className="container">
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                        gap: '3rem',
                        alignItems: 'start'
                    }}>
                        {/* Contact Form */}
                        <div className="card card-gradient">
                            <h3 className="mb-lg">Send Us a Message</h3>
                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="name">Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        className="form-input"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Your name"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="email">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className="form-input"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="your@email.com"
                                        pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                                        title="Enter a valid email address"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="subject">Subject</label>
                                    <select
                                        id="subject"
                                        name="subject"
                                        className="form-input"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="" disabled>Select a service</option>
                                        {SERVICE_OPTIONS.map((service) => (
                                            <option key={service} value={service}>{service}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="message">Message</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        className="form-textarea"
                                        value={formData.message}
                                        onChange={handleChange}
                                        placeholder="Tell us about your project or inquiry..."
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    style={{ width: '100%' }}
                                    disabled={status.state === 'sending'}
                                >
                                    {status.state === 'sending' ? 'Sending...' : 'Send Message →'}
                                </button>

                                {status.message && (
                                    <p
                                        style={{
                                            marginTop: '1rem',
                                            fontWeight: '500',
                                            color: status.state === 'success' ? '#16a34a' : '#dc2626'
                                        }}
                                    >
                                        {status.message}
                                    </p>
                                )}
                            </form>
                        </div>

                        {/* Contact Info */}
                        <div>
                            <h3 className="mb-lg">Contact Information</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                {contactInfo.map((info, index) => (
                                    <div key={index} className="card" style={{ padding: '1.5rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                            <div style={{
                                                width: '48px',
                                                height: '48px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: 'rgba(139, 92, 246, 0.1)',
                                                borderRadius: '0.75rem',
                                                fontSize: '1.5rem'
                                            }}>
                                                {info.icon}
                                            </div>
                                            <div>
                                                <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                                    {info.title}
                                                </p>
                                                {info.link ? (
                                                    <a href={info.link} style={{ fontWeight: '500' }}>
                                                        {info.value}
                                                    </a>
                                                ) : (
                                                    <p style={{ fontWeight: '500' }}>{info.value}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/*
                              NOTE: "Follow Us" social links removed per Khalil's request (2026-05-29).
                              Keep this block to re-add later once social accounts are live.
                              Update the hrefs before un-commenting.

                            <div className="mt-xl">
                                <h4 className="mb-md">Follow Us</h4>
                                <div className="social-links">
                                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">𝕏</a>
                                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">in</a>
                                    <a href="https://github.com" target="_blank" rel="noopener noreferrer" aria-label="GitHub">⌂</a>
                                </div>
                            </div>
                            */}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
